import { create } from "zustand"
import {
  VIEW_REGISTRY,
  getViewFromPathname,
  type ViewId,
  type ViewDefinition,
} from "@/features/views/viewRegistry"
import { useTagFilterStore } from "@/features/tags/tagFilterStore"
import { useViewFilterStore } from "@/features/views/stores/viewFilterStore"
import { useUndoStore } from "@/shared/undo"
import type { DragSourceInfo } from "@/features/dnd/dndConstraints"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ViewContextState {
  // ── Derived from router (set by useViewContextSync) ──
  viewId: ViewId
  containerId: string | null
  viewDef: ViewDefinition

  // ── Capabilities (derived from viewDef on setView) ──
  isSortable: boolean
  allowCreate: boolean
  indexField: "startAtIndex" | "index"

  // ── Ephemeral UI state ──
  /** Task detail open (?tid= in URL) */
  activeTaskId: string | null
  /** Multi-select */
  selectedTaskIds: string[]
  /** Anchor for Shift+Click range selection */
  lastClickedTaskId: string | null
  /** Sidebar multi-select */
  selectedSidebarIds: string[]
  /** Anchor for Shift+Click range selection in sidebar */
  lastClickedSidebarId: string | null
  /** Current view's task IDs in display order — set by ViewShell for Shift+Click range */
  visibleTaskIds: string[]
  /** Internal: base visible IDs (before evening overlay) */
  _baseVisibleTaskIds: string[]
  /** Internal: evening task IDs appended after a separator */
  _eveningTaskIds: string[]
  /** IDs of sections in the visible list — used for section-aware Ctrl+A */
  visibleSectionIds: Set<string>
  /** All task IDs per container group (including hidden behind "Show more") */
  groupedAllTaskIds: string[][]
  /** Internal: base grouped IDs (before evening overlay) */
  _baseGroupedAllTaskIds: string[][]
  /** Internal: evening grouped IDs */
  _eveningGroupedAllTaskIds: string[][]
  /** DnD in progress — true only while actively dragging */
  isDragging: boolean
  /** The task ID currently being dragged (null when not dragging) */
  activeDragId: string | null
  /** IDs of companion items being dragged along (section children, multi-select) */
  dragCompanionIds: string[]
  /** Type info about the dragged items — used for sidebar highlight constraints */
  dragSourceInfo: DragSourceInfo | null
  /** Brief guard after drag ends — prevents PullToRefresh from triggering on touchend */
  dragProtect: boolean
  /** Sidebar drop target hovered during drag (e.g. "today", "project:abc") */
  sidebarDropTarget: string | null
  /** True when pointer is anywhere inside the sidebar panel during drag */
  isOverSidebar: boolean
  /** Swipe gesture in progress */
  isSwiping: boolean
  /** Mobile sidebar drawer open */
  sidebarOpen: boolean
  /** Desktop sidebar collapsed (persisted to localStorage) */
  desktopSidebarCollapsed: boolean
  /** Search modal open */
  searchOpen: boolean
  /** AI chat panel open */
  aiChatOpen: boolean

  // ── Pickers (unified visibility + targets) ──
  schedulePicker: { taskIds: string[]; anchor?: DOMRect } | null
  deadlinePicker: { taskIds: string[]; anchor?: DOMRect } | null
  /** Unified When sheet — opened for a single task */
  whenSheetTaskId: string | null
  /** Move-to container picker (opened from keyboard shortcut or action panel) */
  movePicker: { taskIds: string[] } | null
  /** Tag picker (opened from keyboard shortcut) */
  tagPicker: { taskIds: string[] } | null
  /** Status picker (opened from keyboard shortcut) */
  statusPicker: { taskIds: string[] } | null
  /** Icon picker for containers */
  iconPicker: { containerId: string } | null

  // ── Actions ──
  setView: (viewId: ViewId, containerId?: string | null) => void
  openTask: (id: string) => void
  closeTask: () => void
  selectTask: (id: string) => void
  /** Atomically clear selection and select a single task */
  setSingleSelection: (id: string) => void
  deselectTask: (id: string) => void
  toggleSelectTask: (id: string) => void
  /** Shift+Click: select range from lastClickedTaskId to target */
  rangeSelectTo: (id: string) => void
  /** Select all tasks within the section of the last-clicked task */
  selectWithinSection: () => boolean
  /** Select all visible tasks within the container group of the last-clicked task */
  selectGroup: () => boolean
  /** Select section + all its children (positional walk to next section/separator) */
  selectSectionBlock: (sectionId: string) => void
  /** Select all visible tasks */
  selectAll: () => void
  clearSelection: () => void
  /** Push current display-order task IDs (called by ViewShell) */
  setVisibleTaskIds: (ids: string[]) => void
  /** Push section IDs for section-aware selection */
  setVisibleSectionIds: (ids: Set<string>) => void
  /** Push all task IDs per container group (including hidden) */
  setGroupedAllTaskIds: (groups: string[][]) => void
  /** Push evening task IDs — appended to visibleTaskIds with a separator */
  setEveningTaskIds: (ids: string[]) => void
  /** Push evening grouped IDs — appended to groupedAllTaskIds */
  setEveningGroupedAllTaskIds: (groups: string[][]) => void
  setDragging: (
    value: boolean,
    activeDragId?: string | null,
    dragSourceInfo?: DragSourceInfo | null,
  ) => void
  setSidebarDropTarget: (target: string | null) => void
  setIsOverSidebar: (value: boolean) => void
  setSwiping: (value: boolean) => void
  setSidebarOpen: (value: boolean) => void
  toggleSidebar: () => void
  setDesktopSidebarCollapsed: (value: boolean) => void
  toggleDesktopSidebar: () => void
  setSearchOpen: (value: boolean) => void
  toggleSearch: () => void
  setAiChatOpen: (value: boolean) => void
  toggleAiChat: () => void
  openSchedulePicker: (taskIds: string[], anchor?: DOMRect) => void
  openDeadlinePicker: (taskIds: string[], anchor?: DOMRect) => void
  closePickers: () => void
  openMovePicker: (taskIds: string[]) => void
  openTagPicker: (taskIds: string[]) => void
  openStatusPicker: (taskIds: string[]) => void
  openIconPicker: (containerId: string) => void
  openWhenSheet: (taskId: string) => void
  closeWhenSheet: () => void

  // ── Sidebar selection ──
  toggleSelectSidebar: (id: string) => void
  rangeSelectSidebar: (id: string, orderedIds: string[]) => void
  clearSidebarSelection: () => void
  setSingleSidebarSelection: (id: string) => void
}

// ---------------------------------------------------------------------------
// Capability helpers
// ---------------------------------------------------------------------------

const NON_SORTABLE_VIEWS: ViewId[] = [
  "logbook",
  "trash",
  "upcoming",
  "search",
  "tag",
  "laterProjects",
]
const NON_CREATABLE_VIEWS: ViewId[] = [
  "logbook",
  "trash",
  "search",
  "tag",
  "laterProjects",
]
const START_AT_INDEX_VIEWS: ViewId[] = [
  "today",
  "evening",
  "upcoming",
  "someday",
]

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

const defaultView = VIEW_REGISTRY.today

/** Delay before clearing isDragging — prevents PtR from triggering on touchend */
let dragTimeout: ReturnType<typeof setTimeout> | undefined

/** Synchronous read of desktop sidebar collapsed state */
function readDesktopSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem("totodo:sidebar-collapsed") === "true"
  } catch {
    return false
  }
}

/** Synchronous read of AI chat open state */
function readAiChatOpen(): boolean {
  try {
    return localStorage.getItem("totodo:ai-chat-open") === "true"
  } catch {
    return false
  }
}

export const useViewContext = create<ViewContextState>()((set, get) => ({
  // Defaults
  viewId: defaultView.id,
  containerId: null,
  viewDef: defaultView,
  isSortable: true,
  allowCreate: true,
  indexField: "startAtIndex" as const,

  activeTaskId: null,
  selectedTaskIds: [],
  lastClickedTaskId: null,
  selectedSidebarIds: [],
  lastClickedSidebarId: null,
  visibleTaskIds: [],
  _baseVisibleTaskIds: [] as string[],
  _eveningTaskIds: [] as string[],
  visibleSectionIds: new Set<string>(),
  groupedAllTaskIds: [],
  _baseGroupedAllTaskIds: [] as string[][],
  _eveningGroupedAllTaskIds: [] as string[][],
  isDragging: false,
  activeDragId: null,
  dragCompanionIds: [],
  dragSourceInfo: null,
  dragProtect: false,
  sidebarDropTarget: null,
  isOverSidebar: false,
  isSwiping: false,
  sidebarOpen: false,
  desktopSidebarCollapsed: readDesktopSidebarCollapsed(),
  searchOpen: false,
  aiChatOpen: readAiChatOpen(),
  schedulePicker: null,
  deadlinePicker: null,
  whenSheetTaskId: null,
  movePicker: null,
  tagPicker: null,
  statusPicker: null,
  iconPicker: null,

  // ── View ──
  setView: (viewId, containerId) => {
    const viewDef = VIEW_REGISTRY[viewId]
    if (!viewDef) return

    const state = get()
    const resolvedContainerId = containerId ?? null

    // Skip if view hasn't actually changed — prevents clearing selection
    // when useViewContextSync re-fires on the same route
    if (state.viewId === viewId && state.containerId === resolvedContainerId) {
      return
    }

    set({
      viewId,
      containerId: resolvedContainerId,
      viewDef,
      isSortable: !NON_SORTABLE_VIEWS.includes(viewId),
      allowCreate: !NON_CREATABLE_VIEWS.includes(viewId),
      indexField: START_AT_INDEX_VIEWS.includes(viewId)
        ? "startAtIndex"
        : "index",
      // Clear transient state on view change
      selectedTaskIds: [],
      selectedSidebarIds: [],
      lastClickedSidebarId: null,
      _eveningTaskIds: [],
      _eveningGroupedAllTaskIds: [],
    })

    // Reset filters on navigation
    useTagFilterStore.getState().reset()
    useViewFilterStore.getState().reset()

    // Clear undo stack — previous view's undos are no longer relevant
    useUndoStore.getState().clear()
  },

  // ── Active task ──
  openTask: (id) => set({ activeTaskId: id }),
  closeTask: () => set({ activeTaskId: null }),

  // ── Selection ──
  selectTask: (id) =>
    set((s) => ({
      selectedTaskIds: s.selectedTaskIds.includes(id)
        ? s.selectedTaskIds
        : [...s.selectedTaskIds, id],
      lastClickedTaskId: id,
    })),
  setSingleSelection: (id) => {
    set({ selectedTaskIds: [id], lastClickedTaskId: id })
  },
  deselectTask: (id) =>
    set((s) => ({
      selectedTaskIds: s.selectedTaskIds.filter((tid) => tid !== id),
    })),
  toggleSelectTask: (id) =>
    set((s) => ({
      selectedTaskIds: s.selectedTaskIds.includes(id)
        ? s.selectedTaskIds.filter((tid) => tid !== id)
        : [...s.selectedTaskIds, id],
      lastClickedTaskId: id,
    })),
  rangeSelectTo: (id) =>
    set((s) => {
      const anchor = s.lastClickedTaskId
      const ids = s.visibleTaskIds
      if (!anchor || ids.length === 0) {
        return {
          selectedTaskIds: s.selectedTaskIds.includes(id)
            ? s.selectedTaskIds
            : [...s.selectedTaskIds, id],
          lastClickedTaskId: id,
        }
      }
      const anchorIdx = ids.indexOf(anchor)
      const targetIdx = ids.indexOf(id)
      if (anchorIdx === -1 || targetIdx === -1) {
        return { lastClickedTaskId: id }
      }
      const start = Math.min(anchorIdx, targetIdx)
      const end = Math.max(anchorIdx, targetIdx)
      const rangeIds = ids
        .slice(start, end + 1)
        .filter((rid) => !rid.startsWith("__group_sep__"))
      const merged = Array.from(new Set([...s.selectedTaskIds, ...rangeIds]))
      return { selectedTaskIds: merged }
    }),
  selectWithinSection: () => {
    const s = get()
    const anchor = s.lastClickedTaskId
    if (!anchor || s.visibleSectionIds.size === 0) return false

    const ids = s.visibleTaskIds
    const anchorIdx = ids.indexOf(anchor)
    if (anchorIdx === -1) return false

    // Walk backward to find section start (a section, separator, or start of list)
    let sectionStart = 0
    for (let i = anchorIdx; i >= 0; i--) {
      if (ids[i]!.startsWith("__group_sep__")) {
        sectionStart = i + 1
        break
      }
      if (s.visibleSectionIds.has(ids[i]!)) {
        sectionStart = i
        break
      }
    }

    // Walk forward to find section end (next section, separator, or end of list)
    let sectionEnd = ids.length - 1
    for (let i = anchorIdx + 1; i < ids.length; i++) {
      if (
        ids[i]!.startsWith("__group_sep__") ||
        s.visibleSectionIds.has(ids[i]!)
      ) {
        sectionEnd = i - 1
        break
      }
    }

    const sectionIds = ids
      .slice(sectionStart, sectionEnd + 1)
      .filter((id) => !id.startsWith("__group_sep__"))
    if (sectionIds.length === 0) return false

    // If the section is already fully selected, return false
    // so the caller can escalate to selectAll()
    const currentSet = new Set(s.selectedTaskIds)
    const alreadySelected =
      sectionIds.length === currentSet.size &&
      sectionIds.every((id) => currentSet.has(id))
    if (alreadySelected) return false

    set({ selectedTaskIds: sectionIds })
    return true
  },
  selectGroup: () => {
    const s = get()
    const anchor = s.lastClickedTaskId
    if (!anchor) return false

    const ids = s.visibleTaskIds
    const anchorIdx = ids.indexOf(anchor)
    if (anchorIdx === -1) return false

    // Walk backward to find group start (separator or start of list)
    let groupStart = 0
    for (let i = anchorIdx - 1; i >= 0; i--) {
      if (ids[i]!.startsWith("__group_sep__")) {
        groupStart = i + 1
        break
      }
    }

    // Walk forward to find group end (separator or end of list)
    let groupEnd = ids.length - 1
    for (let i = anchorIdx + 1; i < ids.length; i++) {
      if (ids[i]!.startsWith("__group_sep__")) {
        groupEnd = i - 1
        break
      }
    }

    // Visible tasks in this group
    const visibleGroupIds = ids
      .slice(groupStart, groupEnd + 1)
      .filter((id) => !id.startsWith("__group_sep__"))
    if (visibleGroupIds.length === 0) return false

    const currentSet = new Set(s.selectedTaskIds)

    // Check if visible group already fully selected
    const visibleAlreadySelected = visibleGroupIds.every((id) =>
      currentSet.has(id),
    )

    if (!visibleAlreadySelected) {
      // Stage 1: select visible tasks in this group
      set({ selectedTaskIds: visibleGroupIds })
      return true
    }

    // Stage 2: expand to ALL tasks in the group (including hidden behind "Show more")
    // Find which groupedAllTaskIds entry contains the anchor
    const allGroup = s.groupedAllTaskIds.find((g) => g.includes(anchor))
    if (allGroup && allGroup.length > 0) {
      const allAlreadySelected =
        allGroup.length === currentSet.size &&
        allGroup.every((id) => currentSet.has(id))
      if (!allAlreadySelected) {
        set({ selectedTaskIds: [...allGroup] })
        return true
      }
    }

    // Already fully selected (including hidden) — return false to escalate
    return false
  },
  selectSectionBlock: (sectionId) => {
    const s = get()
    const ids = s.visibleTaskIds
    const sectionIdx = ids.indexOf(sectionId)
    if (sectionIdx === -1) return

    // Collect section + all children until next section or separator
    const blockIds: string[] = [sectionId]
    for (let i = sectionIdx + 1; i < ids.length; i++) {
      const id = ids[i]!
      if (id.startsWith("__group_sep__") || s.visibleSectionIds.has(id)) break
      blockIds.push(id)
    }

    // Toggle: if all block IDs are already selected, remove them (deselect).
    // Otherwise, add (union) the block IDs to the current selection.
    const selected = s.selectedTaskIds
    const blockSet = new Set(blockIds)
    const allAlreadySelected =
      blockIds.length > 0 && blockIds.every((id) => selected.includes(id))

    if (allAlreadySelected) {
      // Deselect this section block — keep everything else
      const remaining = selected.filter((id) => !blockSet.has(id))
      set({ selectedTaskIds: remaining, lastClickedTaskId: null })
    } else {
      // Add this block to existing selection (union, preserve order)
      const existingSet = new Set(selected)
      const merged = [...selected]
      for (const id of blockIds) {
        if (!existingSet.has(id)) merged.push(id)
      }
      set({ selectedTaskIds: merged, lastClickedTaskId: sectionId })
    }
  },
  selectAll: () =>
    set((s) => ({
      selectedTaskIds: s.visibleTaskIds.filter(
        (id) => !id.startsWith("__group_sep__"),
      ),
    })),
  clearSelection: () => {
    set({ selectedTaskIds: [], lastClickedTaskId: null })
  },
  setVisibleTaskIds: (ids) =>
    set((s) => {
      const evening = s._eveningTaskIds
      return {
        _baseVisibleTaskIds: ids,
        visibleTaskIds:
          evening.length > 0
            ? [...ids, "__group_sep__evening", ...evening]
            : ids,
      }
    }),
  setVisibleSectionIds: (ids) => set({ visibleSectionIds: ids }),
  setGroupedAllTaskIds: (groups) =>
    set((s) => ({
      _baseGroupedAllTaskIds: groups,
      groupedAllTaskIds: [...groups, ...s._eveningGroupedAllTaskIds],
    })),
  setEveningTaskIds: (ids) =>
    set((s) => {
      const base = s._baseVisibleTaskIds
      return {
        _eveningTaskIds: ids,
        visibleTaskIds:
          ids.length > 0
            ? [...base, "__group_sep__evening", ...ids]
            : [...base],
      }
    }),
  setEveningGroupedAllTaskIds: (groups) =>
    set((s) => ({
      _eveningGroupedAllTaskIds: groups,
      groupedAllTaskIds: [...s._baseGroupedAllTaskIds, ...groups],
    })),

  // ── Drag state ──
  setDragging: (value, activeDragId, dragSourceInfo) => {
    if (dragTimeout != null) {
      clearTimeout(dragTimeout)
      dragTimeout = undefined
    }
    if (value) {
      set({
        isDragging: true,
        dragProtect: true,
        activeDragId: activeDragId ?? null,
        dragSourceInfo: dragSourceInfo ?? null,
      })
    } else {
      // Clear isDragging + activeDragId immediately so companion opacity resets.
      // NOTE: sidebarDropTarget is NOT cleared here — DndListProvider reads it
      // synchronously in handleDragEnd before the useSidebarDragOver cleanup
      // effect runs. The hook clears it when isDragging toggles to false.
      set({ isDragging: false, activeDragId: null, dragCompanionIds: [], dragSourceInfo: null })
      // Brief guard for PullToRefresh — prevents PtR from triggering on
      // touchend of the drag gesture (mirrors v1's 100ms debounce)
      dragTimeout = setTimeout(() => {
        set({ dragProtect: false })
        dragTimeout = undefined
      }, 100)
    }
  },

  // ── Sidebar drop target ──
  setSidebarDropTarget: (target) => set({ sidebarDropTarget: target }),
  setIsOverSidebar: (value) => set({ isOverSidebar: value }),

  // ── Swipe state ──
  setSwiping: (value) => set({ isSwiping: value }),

  // ── Sidebar ──
  setSidebarOpen: (value) => set({ sidebarOpen: value }),
  toggleSidebar: () => set((s) => ({ sidebarOpen: !s.sidebarOpen })),
  setDesktopSidebarCollapsed: (value) => {
    try {
      localStorage.setItem("totodo:sidebar-collapsed", String(value))
    } catch {}
    set({ desktopSidebarCollapsed: value })
  },
  toggleDesktopSidebar: () => {
    const next = !get().desktopSidebarCollapsed
    try {
      localStorage.setItem("totodo:sidebar-collapsed", String(next))
    } catch {}
    set({ desktopSidebarCollapsed: next })
  },

  // ── Search ──
  setSearchOpen: (value) => set({ searchOpen: value }),
  toggleSearch: () => set((s) => ({ searchOpen: !s.searchOpen })),

  // ── AI Chat ──
  setAiChatOpen: (value) => {
    try {
      localStorage.setItem("totodo:ai-chat-open", String(value))
    } catch {}
    set({ aiChatOpen: value })
  },
  toggleAiChat: () => {
    const next = !get().aiChatOpen
    try {
      localStorage.setItem("totodo:ai-chat-open", String(next))
    } catch {}
    set({ aiChatOpen: next })
  },

  // ── Pickers ──
  openSchedulePicker: (taskIds, anchor) =>
    set({ schedulePicker: { taskIds, anchor } }),
  openDeadlinePicker: (taskIds, anchor) =>
    set({ deadlinePicker: { taskIds, anchor } }),
  closePickers: () =>
    set({
      schedulePicker: null,
      deadlinePicker: null,
      movePicker: null,
      tagPicker: null,
      statusPicker: null,
      iconPicker: null,
    }),
  openMovePicker: (taskIds) => set({ movePicker: { taskIds } }),
  openTagPicker: (taskIds) => set({ tagPicker: { taskIds } }),
  openStatusPicker: (taskIds) => set({ statusPicker: { taskIds } }),
  openIconPicker: (containerId) => set({ iconPicker: { containerId } }),
  openWhenSheet: (taskId) => set({ whenSheetTaskId: taskId }),
  closeWhenSheet: () => set({ whenSheetTaskId: null }),

  // ── Sidebar selection ──
  toggleSelectSidebar: (id) =>
    set((s) => ({
      selectedSidebarIds: s.selectedSidebarIds.includes(id)
        ? s.selectedSidebarIds.filter((sid) => sid !== id)
        : [...s.selectedSidebarIds, id],
      lastClickedSidebarId: id,
    })),
  rangeSelectSidebar: (id, orderedIds) =>
    set((s) => {
      const anchor = s.lastClickedSidebarId
      if (!anchor || orderedIds.length === 0) {
        return {
          selectedSidebarIds: s.selectedSidebarIds.includes(id)
            ? s.selectedSidebarIds
            : [...s.selectedSidebarIds, id],
          lastClickedSidebarId: id,
        }
      }
      const anchorIdx = orderedIds.indexOf(anchor)
      const targetIdx = orderedIds.indexOf(id)
      if (anchorIdx === -1 || targetIdx === -1) {
        return { lastClickedSidebarId: id }
      }
      const start = Math.min(anchorIdx, targetIdx)
      const end = Math.max(anchorIdx, targetIdx)
      const rangeIds = orderedIds.slice(start, end + 1)
      const merged = Array.from(new Set([...s.selectedSidebarIds, ...rangeIds]))
      return { selectedSidebarIds: merged }
    }),
  clearSidebarSelection: () =>
    set({ selectedSidebarIds: [], lastClickedSidebarId: null }),
  setSingleSidebarSelection: (id) =>
    set({ selectedSidebarIds: [id], lastClickedSidebarId: id }),
}))

// ---------------------------------------------------------------------------
// Non-React access helpers
// ---------------------------------------------------------------------------

/**
 * Resolve view from pathname — convenience for useViewContextSync.
 * Returns { viewId, containerId } or defaults to "today".
 *
 * NOTE: Extracts containerId directly from the pathname (not from
 * useParams) because this is called from AppLayout which is ABOVE
 * the <Route> definitions — useParams() can't see :id at that level.
 */
export function resolveViewFromPathname(
  pathname: string,
  _params?: Record<string, string | undefined>,
): { viewId: ViewId; containerId: string | null } {
  const viewDef = getViewFromPathname(pathname)
  if (!viewDef) return { viewId: "today", containerId: null }

  let containerId: string | null = null
  if (
    viewDef.id === "project" ||
    viewDef.id === "list" ||
    viewDef.id === "area"
  ) {
    // Extract ID from pathname: "/project/:id" → segments[2]
    const segments = pathname.split("/")
    containerId = segments[2] || null
  }

  return { viewId: viewDef.id, containerId }
}

// Expose store for E2E testing
if (import.meta.env.DEV) {
  ;(window as any).__viewContextStore = useViewContext
}
